import React from 'react';
import { AVAILABLE_ROLES, AVAILABLE_AREAS, AREA_GROUPS, UserRole, formatSalary } from './constants';
import { useAuth } from '../../contexts/AuthContext';

const IOS_APP_URL = 'https://apps.apple.com/us/app/sweetgrassfieldapp/id6758865761';

export default function CreateTeamMemberModal({
  isOpen,
  onClose,
  formData,
  setFormData,
  onSubmit,
  isLoading,
  error,
  onSwitchToInvite
}) {
  const { user: currentUser } = useAuth();

  if (!isOpen) return null;

  const webAppUrl = typeof window !== 'undefined' ? `${window.location.origin}/login` : '/login';

  const handleSwitchToInvite = () => {
    if (typeof onSwitchToInvite === 'function') onSwitchToInvite();
  };

  const handleSwitchToInviteKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSwitchToInvite();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'salary') {
      // Format salary input
      const formatted = formatSalary(value);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Helper function to handle area checkbox changes
  const handleAreaChange = (areaValue, checked) => {
    setFormData(prev => ({
      ...prev,
      areas: checked
        ? [...prev.areas, areaValue]
        : prev.areas.filter(area => area !== areaValue)
    }));
  };

  // Helper function to handle area group selections
  const handleAreaGroupChange = (groupValue, checked) => {
    const group = AREA_GROUPS.find(g => g.value === groupValue);
    if (!group) return;

    setFormData(prev => {
      let newAreas;
      if (checked) {
        // Add all group areas (avoiding duplicates)
        newAreas = [...new Set([...prev.areas, ...group.areas])];
      } else {
        // Remove all group areas
        newAreas = prev.areas.filter(area => !group.areas.includes(area));
      }
      return {
        ...prev,
        areas: newAreas
      };
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-lg font-medium text-gray-900">Add Team Member</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 pt-5 space-y-4">
          <div className="p-4 rounded-lg border border-indigo-200 bg-indigo-50">
            <div className="flex items-start gap-3">
              <svg
                className="h-6 w-6 text-indigo-600 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h4 className="font-semibold text-indigo-900">Preferred method: Invite the user</h4>
                <p className="text-sm text-indigo-800 mt-1">
                  Instead of creating an account manually, send an email invitation. The new user will receive a secure link and their own temporary password — you never have to handle or share credentials yourself.
                </p>
                {typeof onSwitchToInvite === 'function' && (
                  <button
                    type="button"
                    onClick={handleSwitchToInvite}
                    onKeyDown={handleSwitchToInviteKeyDown}
                    tabIndex={0}
                    aria-label="Switch to the Invite User flow"
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send Invite Instead
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">How the new team member will access the app</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <a
                href={IOS_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download the Sweetgrass Field App on the App Store"
                className="block p-3 rounded-lg border border-sky-200 bg-sky-50 hover:bg-sky-100 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
              >
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-sky-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div>
                    <div className="text-sm font-semibold text-sky-900">iOS App (iPhone &amp; iPad)</div>
                    <div className="text-xs text-sky-800 mt-1">
                      Recommended for Apple users. Native mobile experience with push notifications and camera-based inspections.
                    </div>
                    <div className="text-xs text-sky-700 mt-1 break-all">{IOS_APP_URL}</div>
                  </div>
                </div>
              </a>

              <a
                href={webAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open the Sweetgrass web app"
                className="block p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-gray-700 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18M12 3a15 15 0 000 18" />
                  </svg>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Web Browser Version</div>
                    <div className="text-xs text-gray-700 mt-1">
                      Works on any modern browser (desktop, laptop, Android, iPad). Full admin tools — best for managers and office staff.
                    </div>
                    <div className="text-xs text-gray-600 mt-1 break-all">{webAppUrl}</div>
                  </div>
                </div>
              </a>
            </div>
          </div>

          <div className="p-3 rounded-lg border border-amber-300 bg-amber-50">
            <div className="flex items-start gap-2">
              <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <p className="text-sm text-amber-900">
                <strong>Security notice:</strong> If you must add this team member manually, do <strong>not</strong> share the password by email or any other unencrypted channel. Deliver it in person or through a secure messenger, and ask the user to change it immediately after their first login.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="p-6 pt-4">
          {error && (
            <div className="mb-4 p-2 text-sm bg-red-50 border border-red-200 text-red-600 rounded">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                />
              </div>
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="text"
                  name="phoneNumber"
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  id="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                />
                <p className="mt-1 text-xs text-amber-700">
                  Never send this password over email. Share it in person or through a secure channel, and have the user change it after first login.
                </p>
              </div>
              {currentUser?.role === UserRole.ADMIN && (
                <div className="md:col-span-2">
                  <label htmlFor="salary" className="block text-sm font-medium text-gray-700">
                    Salary (Weekly Salary)
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="text"
                      name="salary"
                      id="salary"
                      value={formData.salary}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      className="block w-full pl-7 pr-3 py-2 rounded-md border border-gray-500 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the weekly salary for this team member (used in reports)
                  </p>
                </div>
              )}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.roles[0] || UserRole.HOUSEKEEPER}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      roles: [e.target.value]
                    }));
                  }}
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                >
                  {AVAILABLE_ROLES.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="locationPreference" className="block text-sm font-medium text-gray-700">
                  Location Preference
                </label>
                <select
                  id="locationPreference"
                  name="locationPreference"
                  value={formData.locationPreference}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                >
                  <option value="">Select a location...</option>
                  {AVAILABLE_AREAS.map(area => (
                    <option key={area.value} value={area.value}>
                      {area.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Work Areas (Select multiple)
              </label>
              
              {/* Quick Group Selections */}
              <div className="mb-3 space-y-2">
                <p className="text-xs text-gray-600">Quick selections:</p>
                {AREA_GROUPS.map(group => {
                  const hasAllGroupAreas = group.areas.every(area => formData.areas.includes(area));
                  return (
                    <label key={group.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={hasAllGroupAreas}
                        onChange={(e) => handleAreaGroupChange(group.value, e.target.checked)}
                        className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                      />
                      <span className="text-sm font-medium text-purple-700">{group.label}</span>
                    </label>
                  );
                })}
              </div>
              
              {/* Individual Area Selections */}
              <div className="space-y-2">
                <p className="text-xs text-gray-600">Individual areas:</p>
                {AVAILABLE_AREAS.map(area => (
                  <label key={area.value} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.areas.includes(area.value)}
                      onChange={(e) => handleAreaChange(area.value, e.target.checked)}
                      className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                    />
                    <span className="text-sm text-gray-700">{area.label}</span>
                  </label>
                ))}
              </div>
              
              {formData.areas.length > 0 && (
                <div className="mt-2 text-xs text-gray-600">
                  Selected: {formData.areas.map(area => 
                    AVAILABLE_AREAS.find(a => a.value === area)?.label || area
                  ).join(', ')}
                </div>
              )}
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 